import React from "react";
import {
  View,
  TextInput,
  Text,
  Keyboard,
  SafeAreaView,
  TouchableOpacity,
} from "react-native";
import { AntDesign } from "@expo/vector-icons";
import { useFormik } from "formik";
import { Button } from "react-native-elements";
import { styles } from "styles/screens/splashScreen";
import AppContext from "contexts/AppContext";
import Loading from "components/Loading";
import { CreateForm } from "forms/schemas/splashForm";
import { ScrollView } from "react-native-gesture-handler";
import { Checkbox } from "react-native-paper";
import MessageModal from "components/MessageModal";
import { getOrder, requestInvoice } from "services/orderService";
import Order, { OrderStatusEnum } from "types/models/Order";


export default (): JSX.Element => {
  const { isLoading, setIsLoading } = React.useContext(AppContext);
  const [requiresFacturaA, setRequiresFacturaA] = React.useState<boolean>(false);
  const [orderIsValid, setOrderIsValid] = React.useState<boolean>(false);
  const [result, setResult] = React.useState<boolean>(false);
  const [showMessageModal, setShowMessageModal] = React.useState<boolean>(false);
  const [message, setMessage] = React.useState<string>('');
  const [order, setOrder] = React.useState<null | Order>(null);

  const onConfirm = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const body = { ...formik.values, ...order, requiresFacturaA: requiresFacturaA };
      const response = await requestInvoice(body);
      if (response.result) {
        setResult(true);
        setMessage('La factura será enviada al email solicitado');
        setShowMessageModal(true);
      }

    } catch (error) {
      console.log("error", error);
    } finally {
      setIsLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      orderId: "",
      email: "",
      cuit: ""
    },
    onSubmit: () => onConfirm(),
    validationSchema: CreateForm(requiresFacturaA),
    validateOnBlur: false,
    validateOnChange: false,
  });

  const onConfirmForm = async (): Promise<void> => {
    Keyboard.dismiss();

    if (!formik.values.orderId) return;
    // Si ya se había seteado el orderId entonces ya vamos a solicitar  la factura
    if (orderIsValid) {
      formik.handleSubmit();
    } else {
      const orderId = parseInt(formik.values.orderId);
      setIsLoading(true);
      try {
        const fullOrder = await getOrder(orderId);

        // si el endpoint responde null es porque no existe la orden
        if (!fullOrder) {
          showErrorMessage('Ups, no encontramos este número de orden. Vuelva a intentarlo');
          return;
        }
        if (!fullOrder.commerce.allowBilling) {
          showErrorMessage('Ups, parece que este comercio no emite facturas');
          return;
        }
        if (fullOrder.invoiceProcessId) {
          const message = `La factura de esta orden fue enviada a este email: ${fullOrder.invoiceEmail}, por favor revíselo nuevamente.`;
          showErrorMessage(message);
          return;
        }

        const invalidStatuses = [OrderStatusEnum.Created, OrderStatusEnum.Incompleted];
        if (invalidStatuses.includes(fullOrder.statusId)) {
          showErrorMessage('La orden no está finalizada o no tiene el pago correspondiente');
          return;
        }

        setOrder(fullOrder);
        setOrderIsValid(true);
      } catch (error) {
        console.log("error", error);
      } finally {
        setIsLoading(false);
      }
    }
  }

  const showErrorMessage = (message: string) => {
    setResult(false);
    setMessage(message)
    setShowMessageModal(true);
    setOrderIsValid(false);
  };

  if (isLoading) {
    return <Loading />;
  }

  const onClose = () => {
    setShowMessageModal(false);
    setOrderIsValid(false);
    formik.resetForm();
  }

  const disableButton = (): boolean => {
    // si el campo de orderId no fue seteado, entonces que esté bloqueado el botón
    return !formik.values.orderId;
  };

  return (
    <SafeAreaView style={{ flex: 1 }}>
      <MessageModal result={result} show={showMessageModal} message={message} onClose={() => onClose()} />
      <ScrollView
        scrollEnabled
        nestedScrollEnabled
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={{ flexGrow: 1, justifyContent: "space-between" }}
        style={[styles.body, { backgroundColor: '#F2F2F2' }]}
      >
        <View style={{ height: "auto", maxHeight: 0 }}>
          <View style={styles.imagesContainer}>
            <Text style={styles.title}>
              ¡Pedí tu <Text style={styles.invoiceTitleColor}>factura</Text>!
            </Text>
          </View>

          {orderIsValid ? (
            <>
              <View style={{ alignItems: "center", marginBottom: 16, paddingLeft: 10 }}>
                <TouchableOpacity style={[styles.descriptionContainer]} onPress={() => onClose()}>
                  <View style={styles.checkboxContainer}>
                    <AntDesign style={{ alignSelf: "flex-start" }} name="leftcircleo" size={32} color="black" />
                    <Text style={[styles.backButtonText]}>Pedir factura de otra orden</Text>
                  </View>
                </TouchableOpacity>
              </View>
              <View style={{ alignItems: "center" }}>
                <View style={[styles.descriptionContainer]}>
                  <View style={styles.checkboxContainer}>
                    <Text>Escriba su correo electrónico para recibir su factura</Text>
                  </View>
                </View>
              </View>
              <View style={{ alignItems: "center" }}>
                <>
                  <TextInput
                    keyboardType="email-address"
                    style={[styles.textInput]}
                    onChangeText={formik.handleChange("email")}
                    placeholder={"ejemplo@ejemplo.com"}
                    value={formik.values.email}
                    placeholderTextColor={'#959393'}
                  ></TextInput>
                  {formik.errors.email && formik.errors.email.length > 0 && (
                    <View style={styles.errorTextContainer}>
                      <Text style={styles.errorText}>{formik.errors.email}</Text>
                    </View>
                  )}
                </>
              </View>

              <View style={{ alignItems: "center" }}>
                <View style={[styles.requiresFacturaAContainer]}>
                  <TouchableOpacity style={styles.checkboxContainer} onPress={() => {
                    setRequiresFacturaA((b) => !b)
                  }}>
                    <Checkbox
                      status={requiresFacturaA ? 'checked' : 'unchecked'}
                      color="#2E44E1"
                    />
                    <Text>Necesito una factura A</Text>
                  </TouchableOpacity>
                </View>
              </View>

              {requiresFacturaA && (
                <View style={{ alignItems: "center" }}>
                  <>
                    <TextInput
                      keyboardType="number-pad"
                      maxLength={11}
                      style={[styles.textInput]}
                      onChangeText={formik.handleChange("cuit")}
                      placeholder={"30123456789"}
                      value={formik.values.cuit}
                      placeholderTextColor={'#959393'}
                    ></TextInput>
                    {formik.errors.cuit && formik.errors.cuit.length > 0 && (
                      <View style={styles.errorTextContainer}>
                        <Text style={styles.errorText}>{formik.errors.cuit}</Text>
                      </View>
                    )}
                  </>
                </View>
              )}
            </>
          ) : (
            <>
              <View style={{ alignItems: "center" }}>
                <View style={[styles.descriptionContainer]}>
                  <View style={styles.checkboxContainer}>
                    <Text style={styles.descriptionText}>Para emitir su factura, ingrese el número de orden</Text>
                  </View>
                </View>
              </View>

              <View style={{ alignItems: "center" }}>
                <>
                  <TextInput
                    keyboardType="number-pad"
                    style={[styles.textInput]}
                    onChangeText={formik.handleChange("orderId")}
                    placeholder={"Número de orden"}
                    value={formik.values.orderId}
                    placeholderTextColor={'#959393'}
                  ></TextInput>
                  {formik.errors.orderId && formik.errors.orderId.length > 0 && (
                    <View style={styles.errorTextContainer}>
                      <Text style={styles.errorText}>{formik.errors.orderId}</Text>
                    </View>
                  )}
                </>
              </View>
            </>
          )}

          <Button
            title="Confirmar"
            type="clear"
            containerStyle={styles.confirmPaymentBtnContainer}
            buttonStyle={[
              styles.confirmPaymentBtn,
              { backgroundColor: "#056EC1" },
            ]}
            titleStyle={[
              styles.confirmPaymentBtnText,
              { color: "#FFF" },
            ]}
            disabled={formik.isSubmitting || disableButton()}
            disabledStyle={[
              styles.confirmPaymentBtn,
              styles.confirmPaymentBtnDisabled,
            ]}
            disabledTitleStyle={styles.confirmPaymentBtnText}
            onPress={onConfirmForm}
          />
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};